"use client";

import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { io, type Socket } from "socket.io-client";

interface Props {
  slug: string;
  // When already-resolved on first render we don't need to listen —
  // the page is already showing the resolved banner. The listener is
  // a no-op in that case but the parent can still mount it harmlessly
  // (server-rendered branches reduce the conditional to a constant).
  alreadyResolved: boolean;
}

interface MarketResolvedEvent {
  marketId: string;
  marketSlug: string;
  outcome: string;
  resolvedOutcomeId: string | null;
  resolvedAt: string;
}

/**
 * STORY-308 phase 2 — live resolution swap.
 *
 * Server-rendered market detail pages don't update without a manual
 * refresh. The `market:resolved` event already exists on the gateway
 * (admin.service.ts emits after the resolveMarket tx commits — see
 * STORY-309 / STORY-308). What was missing was a frontend consumer.
 *
 * This component is mounted on the market detail page (`force-dynamic`,
 * so server-rendered every request). When the resolution event lands:
 *   1. We trigger `router.refresh()` so the App Router re-runs the
 *      page's server fetch. The next render shows the resolved banner
 *      with the right outcome + resolution notes.
 *   2. The component does NOT optimistically inject UI itself — that
 *      would create two source-of-truth surfaces (server-rendered +
 *      ws-derived) that could drift on reconnect. router.refresh() is
 *      the simplest unified path.
 *
 * Reconnect handling: socket.io's auto-reconnect is already enabled.
 * On `connect` we always trigger a refresh so any events missed during
 * the disconnect (rare but possible) are covered by the next page
 * render. Idempotent — re-rendering a non-resolved market is a no-op
 * cost-wise for the user.
 *
 * Renders nothing visible.
 */
export function MarketResolvedListener({ slug, alreadyResolved }: Props) {
  const router = useRouter();

  useEffect(() => {
    if (alreadyResolved) return;
    const apiBase = process.env.NEXT_PUBLIC_API_URL;
    if (!apiBase) return;
    const url = apiBase.replace(/\/$/, "");

    const socket: Socket = io(url, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    socket.on("connect", () => {
      socket.emit("subscribe", { slug });
      // Reconnect-resume: refresh on every (re)connect so missed
      // resolution events during a disconnect window are picked up by
      // the next server render.
      router.refresh();
    });

    socket.on("market:resolved", (event: MarketResolvedEvent) => {
      if (event.marketSlug !== slug) return;
      router.refresh();
    });

    return () => {
      socket.emit("unsubscribe", { slug });
      socket.disconnect();
    };
  }, [slug, alreadyResolved, router]);

  return null;
}
